import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import type { Config } from '../../shared/types';
import { useThemeMode } from '../../../../contexts/ThemeProvider';

interface Props {
  config: Config;
  contentOnRight: boolean;
}

const ModernBrandingSection: React.FC<Props> = ({ config, contentOnRight }) => {
  const { mode } = useThemeMode();
  const brandingConfig = config?.login?.branding || {};
  const primaryColor = config?.theme?.primary || '#ec027c';
  
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const mainHeading = brandingConfig.mainHeading || '';

  useEffect(() => {
    if (!mainHeading) return;
    
    setDisplayText('');
    setIsComplete(false);
    
    let index = 0;
    const interval = setInterval(() => {
      if (index <= mainHeading.length) {
        setDisplayText(mainHeading.slice(0, index));
        index++;
      } else {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, 140); 
    
    return () => clearInterval(interval);
  }, [mainHeading]);

  return (
    <motion.div
      initial={{ opacity: 0, x: contentOnRight ? 50 : -50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{ flex: 1, display: 'flex', alignItems: 'center' }}
    >
      <Box
        sx={{
          color: 'white',
          maxWidth: 600,
          pl: { xs: 0, md: 8 }
        }}
      >
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <Box sx={{ position: 'relative', mb: 3 }}>
            <Box
              sx={{
                fontSize: { xs: '2rem', sm: '3rem', md: '4.5rem', lg: '6rem' },
                fontWeight: 900,
                lineHeight: 0.9,
                mb: 2,
                letterSpacing: '-0.02em',
                color: '#ffffff',
                textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                fontFamily: 'inherit',
                minHeight: { xs: '3.6rem', sm: '4.8rem', md: '6.6rem', lg: '7.8rem' },
                display: 'block',
                position: 'relative'
              }}
            >
              {displayText}
              <motion.span
                animate={{ opacity: isComplete ? 0 : [1, 0] }}
                transition={{ 
                  duration: 0.8,
                  repeat: isComplete ? 0 : Infinity,
                  repeatType: 'reverse'
                }}
                style={{
                  color: primaryColor,
                  fontWeight: 300,
                  marginLeft: '2px'
                }}
              >
                |
              </motion.span>
            </Box>
            
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: isComplete ? 1 : 0 }}
              transition={{ 
                duration: 1,
                delay: isComplete ? 0.1 : 0,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              style={{ 
                transformOrigin: 'left center',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  height: 4,
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}60, transparent)`,
                  borderRadius: 2,
                  opacity: 0.8,
                  width: { xs: 150, sm: 200, md: 500 }
                }}
              />
            </motion.div>
          </Box>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ 
            opacity: isComplete ? 1 : 0, 
            y: isComplete ? 0 : 30 
          }} 
          transition={{ 
            duration: 0.8, 
            delay: isComplete ? 1 : 0,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 400,
              mb: 4,
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              color: 'rgba(255,255,255,0.9)',
              textShadow: '0 2px 4px rgba(0,0,0,0.4)',
              lineHeight: 1.4,
              maxWidth: 480
            }}
          >
            {brandingConfig.subHeading}
          </Typography>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ 
            opacity: isComplete ? 1 : 0, 
            y: isComplete ? 0 : 20 
          }} 
          transition={{ 
            duration: 0.8, 
            delay: isComplete ? 1.2 : 0,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.6,
              color: 'rgba(255,255,255,0.8)',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              maxWidth: 450
            }}
          >
            {brandingConfig.description}
          </Typography>
        </motion.div>
      </Box>
    </motion.div>
  );
};

export default ModernBrandingSection;